/**
 * Pre-rendered voice clips (docs/VOICE.md). Shared by the render pipeline (tools/voice) and the client player:
 *   - `speechKey(text)`: the manifest key of a spoken line = a 53-bit hash of the normalised plain text, so the client
 *     can resolve exactly what `speak()` receives without shipping the text;
 *   - `VoiceManifest`: per locale + content version, key → [clip file, duration ms] plus slot templates
 *     ("Hi {name}!" → a name-free variant, and cut points around the slot for later name stitching);
 *   - `resolveSpeech(manifest, text)`: exact line → template variant → a sequence of known sentences (joined lines such
 *     as feedback headline + nudge). `null` = no clip, the caller falls back to Web Speech.
 * Pure: no DOM, no I/O.
 */
import { plainText } from './text';

/** A clip: file id (R2 key `voice/<locale>/<file>.m4a`) + duration in ms. */
export type VoiceClip = [file: string, ms: number];

export interface VoiceTemplate {
  /** Literal parts and slot names alternating: even index = literal text (normalised), odd index = slot name. */
  parts: string[];
  /** The slot-free variant ("You finished the whole lesson! I'm so proud."). */
  clip: VoiceClip;
  /** Fixed segments around a single `{name}` slot (rendered with a placeholder name, cut at word timestamps). */
  seg?: { pre?: VoiceClip; post?: VoiceClip };
}

export interface VoiceManifest {
  v: 1;
  locale: string;
  /** Content version: hash of every line + voice of this locale (changes when content or casting changes). */
  version: string;
  /** Normalised-text key (speechKey) → clip. */
  clips: Record<string, VoiceClip>;
  templates?: VoiceTemplate[];
  /** ISO time of the render batch (informational). */
  at?: string;
}

/** Normalised spoken text: emphasis markup removed, NFC, single spaces, trimmed. */
export function normalizeSpeech(text: string): string {
  return plainText(text).normalize('NFC').replace(/\s+/g, ' ').trim();
}

/** cyrb53 (public domain, bryc): fast 53-bit string hash. */
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** Manifest key of a spoken line (stable across platforms; same function in the render pipeline). */
export const speechKey = (text: string): string => cyrb53(normalizeSpeech(text)).toString(36);

/** Split a template string ("Hi {name}!") into alternating literal / slot parts (normalised literals). */
export function templateParts(template: string): string[] {
  const out: string[] = [];
  const re = /\{(\w+)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  const norm = (s: string) => plainText(s).normalize('NFC').replace(/\s+/g, ' ');
  while ((m = re.exec(template))) {
    out.push(norm(template.slice(last, m.index)), m[1]!);
    last = m.index + m[0].length;
  }
  out.push(norm(template.slice(last)));
  if (out.length) {
    out[0] = out[0]!.replace(/^\s+/, '');
    out[out.length - 1] = out[out.length - 1]!.replace(/\s+$/, '');
  }
  return out;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const reCache = new WeakMap<VoiceTemplate, RegExp>();
function templateRe(t: VoiceTemplate): RegExp {
  let re = reCache.get(t);
  if (!re) {
    re = new RegExp('^' + t.parts.map((p, i) => (i % 2 ? '(.+?)' : esc(p))).join('') + '$', 'u');
    reCache.set(t, re);
  }
  return re;
}

/** Slot values when the (normalised) text is an instance of the template, else null. */
export function matchTemplate(t: VoiceTemplate, text: string): Record<string, string> | null {
  const m = templateRe(t).exec(normalizeSpeech(text));
  if (!m) return null;
  const out: Record<string, string> = {};
  for (let i = 1; i < t.parts.length; i += 2) out[t.parts[i]!] = m[(i + 1) / 2]!;
  return out;
}

/** Sentence boundaries: after . ! ? … 。！？ (+ closing quotes), followed by space or (CJK) directly. */
export function sentenceSpans(text: string): [number, number][] {
  const out: [number, number][] = [];
  const re = /[.!?…。！？]+["”’」』)]*\s*/gu;
  let start = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const end = m.index + m[0].length;
    if (end >= text.length || /\s/.test(m[0]) || /[。！？]/.test(m[0])) {
      out.push([start, end]);
      start = end;
    }
  }
  if (start < text.length && text.slice(start).trim()) out.push([start, text.length]);
  return out;
}

export interface SpeechPlan {
  clips: VoiceClip[];
  /** exact = the whole line; template = a slot-free variant; seq = several known lines in a row. */
  how: 'exact' | 'template' | 'seq';
  ms: number;
}

/** Gap between clips of a sequence (ms). */
export const SEQ_GAP_MS = 120;

function single(m: VoiceManifest, text: string): { clip: VoiceClip; how: 'exact' | 'template' } | null {
  const c = m.clips[speechKey(text)];
  if (c) return { clip: c, how: 'exact' };
  for (const t of m.templates ?? []) if (matchTemplate(t, text)) return { clip: t.clip, how: 'template' };
  return null;
}

/** Resolve a line to clips (see file comment); null when any part has no clip. */
export function resolveSpeech(m: VoiceManifest | null | undefined, text: string): SpeechPlan | null {
  if (!m) return null;
  const clean = normalizeSpeech(text);
  if (!clean) return null;
  const one = single(m, clean);
  if (one) return { clips: [one.clip], how: one.how, ms: one.clip[1] };
  const spans = sentenceSpans(clean);
  if (spans.length < 2) return null;
  // Greedy: longest run of sentences from i that is a known line.
  const clips: VoiceClip[] = [];
  let i = 0;
  while (i < spans.length) {
    let hit: VoiceClip | null = null;
    let j = spans.length - 1;
    for (; j >= i; j--) {
      if (i === 0 && j === spans.length - 1) continue;
      const r = single(m, clean.slice(spans[i]![0], spans[j]![1]));
      if (r) {
        hit = r.clip;
        break;
      }
    }
    if (!hit) return null;
    clips.push(hit);
    i = j + 1;
  }
  return { clips, how: 'seq', ms: clips.reduce((a, c) => a + c[1], 0) + SEQ_GAP_MS * (clips.length - 1) };
}

/** Every clip file of a manifest (prefetch / offline download). */
export const manifestFiles = (m: VoiceManifest): string[] => [...new Set([...Object.values(m.clips), ...(m.templates ?? []).map((t) => t.clip)].map((c) => c[0]))];

/* ------------------------------------------------ speakable text ------------------------------------------------ */

type SpeakRule = [RegExp, string];
const QUOTED_OMEGA = /["“”'‘’「」]?ω["“”'‘’「」]?/g;
/**
 * Per-language readings of symbols a TTS voice reads wrongly or not at all ("ω" mouth → "omega", → , =, &, ×, ～).
 * Order matters (specific phrases first). The base language of a locale picks its table; unknown → English.
 */
const SPEAK_RULES: Record<string, SpeakRule[]> = {
  en: [
    [/\ban\s+["“]?ω["”]?\s+mouth/gi, 'a little w-shaped mouth'],
    [QUOTED_OMEGA, 'w-shaped'],
    [/\s*→\s*/g, ', then '],
    [/\s+=\s+/g, ' means '],
    [/\s*&\s*/g, ' and '],
    [/\s*[×✕]\s*/g, ' times '],
  ],
  vi: [
    [/(chữ|hình)\s+["“]?ω["”]?/gi, '$1 w'],
    [QUOTED_OMEGA, 'chữ w'],
    [/\s*→\s*/g, ', rồi '],
    [/\s+=\s+/g, ' là '],
    [/\s*&\s*/g, ' và '],
    [/\s*[×✕]\s*/g, ' nhân '],
  ],
  zh: [
    [QUOTED_OMEGA, 'W'],
    [/\s*→\s*/g, '，然后'],
    [/\s*=\s*/g, '就是'],
    [/\s*&\s*/g, '和'],
    [/\s*[×✕]\s*/g, '乘'],
  ],
};
// Emoji / pictographs (+ their joiners and variation selectors) and decorative tildes are never spoken.
const SILENT = /[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}‍︎️⃣]|[～~]+(?=[\s。！？!?.,，]|$)/gu;

/**
 * The text a voice should SAY for a displayed line: symbols read naturally per language, emoji dropped, spacing tidied.
 * The manifest key stays `speechKey(display text)` — only the rendered audio (and the Web Speech fallback) use this.
 */
export function speakableText(text: string, locale: string): string {
  const base = locale.toLowerCase().startsWith('zh') ? 'zh' : locale.toLowerCase().startsWith('vi') ? 'vi' : 'en';
  let s = normalizeSpeech(text).replace(SILENT, '');
  for (const [re, to] of SPEAK_RULES[base] ?? SPEAK_RULES.en!) s = s.replace(re, to);
  return s
    .replace(/\s+([,.!?;:，。！？；：])/g, '$1')
    .replace(/([,，])\s*\1+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}
